/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { Grid, Tab, Tabs } from '@mui/material';

import { TabPanel } from '../../../../components/TabPanel/TabPanel';
import { Namespaces } from '../../../../i18n';
import { useNavTabsStyles } from '../../../../styles/navigationTabs';
import { ClusterContinuousBackup } from '../../../../types/cluster/ClusterContinuousBackup';
import { ClusterLogicalResponsePayload } from '../../../../types/cluster/ClusterLogicalResponsePayload';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { fetchClusterDetails } from '../../../../utils/queryFns';
import { useUtcFormat } from '../../../../utils/useUtcFormat';
import { createBackupsTabStyles } from './BackupsTab.styles';
import { BackupsContinuous } from './components/BackupsContinuous/BackupsContinuous';
import { BackupsLogical } from './components/BackupsLogical/BackupsLogical';
import { SkeletonBackupsContinuous } from './components/SkeletonBackupsContinuous/SkeletonBackupsContinuous';
import { SkeletonBackupsLogical } from './components/SkeletonBackupsLogical/SkeletonBackupsLogical';

type Props = {
  identificationData: UpdateClusterDetailsParameterIdentification;
  isSpinning: boolean;
  continuousBackups: ClusterContinuousBackup[];
  logicalBackups: ClusterLogicalResponsePayload[];
  isFetchingLogicalBackups: boolean;
};

export const BackupsTab = memo<Props>(props => {
  const { identificationData, isSpinning, continuousBackups, logicalBackups, isFetchingLogicalBackups } = props;
  const { t } = useTranslation(Namespaces.Cluster);
  const classes = createBackupsTabStyles();
  const tabsClasses = useNavTabsStyles();
  const [value, setValue] = useState<number>(0);
  const a11yProps = (index: number) => ({
    id: `scrollable-auto-tab-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-${index}`,
  });
  const { utcLocal, utcServer } = useUtcFormat();

  const clusterDetailsQuery = useQuery(
    [
      fetchClusterDetails.key,
      {
        ...identificationData,
      },
    ],
    fetchClusterDetails.fn,
    {
      refetchOnWindowFocus: false,
    },
  );
  const details = useMemo(() => clusterDetailsQuery.data?.payload, [clusterDetailsQuery.data]);

  const handleChangeTab = (_evt: React.ChangeEvent<object>, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Grid classes={{ root: classes.root }}>
      <Tabs
        aria-label="cluster details tabs"
        indicatorColor="primary"
        scrollButtons={false}
        value={value}
        classes={{ root: tabsClasses.root }}
        className={classes.tabs}
        onChange={handleChangeTab}
        TabIndicatorProps={{ style: { display: 'none' } }}
        orientation="horizontal"
      >
        <Tab
          data-cy="cluster-backups-continuous-tab"
          label={t('details.backups.continuous')}
          icon={<AccessTimeIcon />}
          iconPosition="start"
        />
        <Tab
          data-cy="cluster-backups-logical-tab"
          label={t(`details.backups.logical`)}
          icon={<SettingsBackupRestoreIcon />}
          {...a11yProps(0)}
          iconPosition="start"
        />
      </Tabs>
      <TabPanel value={value} index={0}>
        {!details ? (
          <SkeletonBackupsContinuous />
        ) : (
          details && (
            <BackupsContinuous
              identificationData={identificationData}
              details={details}
              isSpinning={isSpinning}
              continuousBackups={continuousBackups}
            />
          )
        )}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {!details ? (
          <SkeletonBackupsLogical />
        ) : (
          details && (
            <BackupsLogical
              identificationData={identificationData}
              details={details}
              isSpinning={isSpinning}
              utcLocal={utcLocal}
              utcServer={utcServer}
              logicalBackups={logicalBackups}
              isFetchingLogicalBackups={isFetchingLogicalBackups}
            />
          )
        )}
      </TabPanel>
    </Grid>
  );
});
